import { Alert, AlertDescription, AlertTitle } from "design-system/atoms/Alert"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Card, CardContent } from "design-system/molecules/Card"
import { Section } from "design-system/molecules/Section"
import { AlertCircle, FileSpreadsheet } from "lucide-react"
import { CompanySelector } from "modules/backoffice/components/CompanySelector"
import { useExportConversationSalesReportMutation } from "modules/backoffice/hooks/conversationSalesReports/useExportConversationSalesReportMutation"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { FormEvent } from "react"

function toDateInputValue(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

function getDefaultFromDate() {
  const date = new Date()
  date.setDate(date.getDate() - 7)

  return toDateInputValue(date)
}

export default function ConversationSalesReports() {
  const { t } = useTranslation()
  const [companyId, setCompanyId] = useState<string | undefined>()
  const [from, setFrom] = useState(getDefaultFromDate)
  const [to, setTo] = useState(() => toDateInputValue(new Date()))
  const { mutateAsync: exportReport, isPending, error } = useExportConversationSalesReportMutation()

  const dateRangeIsValid = useMemo(() => from.length > 0 && to.length > 0 && from <= to, [from, to])
  const canSubmit = companyId !== undefined && dateRangeIsValid && !isPending

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!companyId || !dateRangeIsValid) return

    await exportReport({
      companyId,
      from,
      to,
    })
  }

  return (
    <div className="space-y-6">
      <Section
        title={t("backoffice.conversationSalesReports.title")}
        description={t("backoffice.conversationSalesReports.description")}
      />

      <Card>
        <CardContent className="pt-6">
          <form
            className="grid gap-5 md:grid-cols-[minmax(260px,1fr)_180px_180px_auto]"
            onSubmit={handleSubmit}
          >
            <div className="space-y-2">
              <Label>{t("backoffice.conversationSalesReports.fields.company")}</Label>
              <CompanySelector
                value={companyId}
                onChange={setCompanyId}
                hasShopifyIntegration
                showAllCompaniesOption={false}
                placeholder={t("backoffice.conversationSalesReports.fields.selectCompany")}
                emptyPlaceholder={t("backoffice.conversationSalesReports.fields.noCompaniesFound")}
                searchPlaceholder={t("backoffice.conversationSalesReports.fields.searchCompany")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="attribution-report-from">
                {t("backoffice.conversationSalesReports.fields.from")}
              </Label>
              <Input
                id="attribution-report-from"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="attribution-report-to">
                {t("backoffice.conversationSalesReports.fields.to")}
              </Label>
              <Input
                id="attribution-report-to"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </div>

            <div className="flex items-end">
              <Button type="submit" disabled={!canSubmit} className="w-full md:w-auto">
                <FileSpreadsheet className="size-4" />
                {isPending
                  ? t("backoffice.conversationSalesReports.actions.requesting")
                  : t("backoffice.conversationSalesReports.actions.request")}
              </Button>
            </div>
          </form>

          {(!dateRangeIsValid || error) && (
            <Alert variant="destructive" className="mt-5">
              <AlertCircle className="size-4" />
              <AlertTitle>{t("backoffice.conversationSalesReports.error.title")}</AlertTitle>
              <AlertDescription>
                {!dateRangeIsValid
                  ? t("backoffice.conversationSalesReports.error.invalidDateRange")
                  : error?.message}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
